// WHAT: Admin-Dashboard-Widget, das die drei wichtigsten Roadmap-Tasks nach kombiniertem Score hervorhebt
// WHY:  Fokussiert Aufmerksamkeit auf die wirkungsstärksten Aufgaben (Impact/Priority/Effort) im Roadmap-Kontext
// DOMAIN: core → dashboard → widgets → admin
// DEPENDENCIES: `useRoadmapTopTasks`-Hook, `WidgetContainer`, TracQ `Badge`, WIDGET_IDS-Registry
// RISKS:
//   - NOTE: Score- und Rank-Berechnung erfolgt im Hook/Backend; Frontend verlässt sich auf korrekt sortierte `topTasks`

import type {
  Roadmap_Item_Status,
  Roadmap_Item_Type,
  Roadmap_Item_Score,
  Roadmap_Item_Priority,
} from "@/domains/admin/roadmap/types";

import { Target, TrendingUp } from "lucide-react";
import { type FC } from "react";

import { Badge } from "@/components/ui";
import { cn } from "@/shared/utils";

import { useRoadmapTopTasks } from "../../../hooks/useRoadmapTopTasks";
import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base/WidgetContainer";

/**
 * Get status color classes for roadmap item status
 *
 * @param status - Roadmap item status
 *
 * @returns {string} CSS classes for status color
 *
 */
const getStatusColor = (status?: Roadmap_Item_Status): string => {
  switch (status) {
    case "done": {
      return "bg-green-500/20 text-green-400 border-green-500/30";
    }
    case "in_progress": {
      return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    }
    case "open": {
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    }
    case "rejected": {
      return "bg-red-500/20 text-red-400 border-red-500/30";
    }
    default: {
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  }
};

/**
 * Get score color class based on score value
 *
 * @param score - Task score value
 *
 * @returns {string} CSS class for score color
 *
 */
const getScoreColor = (score: number): string => {
  if (score >= 8) {
    return "text-red-400";
  }
  if (score >= 6) {
    return "text-orange-400";
  }
  if (score >= 4) {
    return "text-yellow-400";
  }
  return "text-green-400";
};

/**
 * Get score badge color classes based on score value
 *
 * @param score - Task score value
 *
 * @returns {string} CSS classes for score badge color
 *
 */
const getScoreBadgeColor = (score: number): string => {
  if (score >= 8) {
    return "bg-red-500/20 text-red-400 border-red-500/30";
  }
  if (score >= 6) {
    return "bg-orange-500/20 text-orange-400 border-orange-500/30";
  }
  if (score >= 4) {
    return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
  }
  return "bg-green-500/20 text-green-400 border-green-500/30";
};

/**
 * WHAT:
 *   Zeigt die Top-3-Roadmap-Tasks basierend auf einem kombinierten Score (Impact + Priority + invertierter Effort)
 *   inklusive Status-, Typ- und Impact/Priority-Badges.
 *
 * WHY:
 *   Hilft Admins/PMs, die wichtigsten anstehenden Aufgaben auf einen Blick zu erkennen und zu priorisieren.
 *
 * BEHAVIOR:
 *   - Nutzt `useRoadmapTopTasks`, um bereits voraggregierte Top-Tasks inklusive Score und Rank zu beziehen.
 *   - Farblich kodiert Score und Status über Hilfsfunktionen (`getScoreColor`, `getStatusColor`, `getScoreBadgeColor`).
 *
 * RISKS:
 *   - NOTE: Anpassungen der Score-Formel müssen im Hook/Backend erfolgen; dieses Widget ist rein darstellend.
 *
 * @returns {JSX.Element} Roadmap top tasks widget component
 *
 */
export const RoadmapTopTasksWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.ROADMAP_TOP_TASKS);
  const { error, isLoading, topTasks } = useRoadmapTopTasks();

  return (
    <WidgetContainer
      description="Top 3 Tasks nach Score (Impact + Priority + Effort)"
      error={error ? "Fehler beim Laden der Roadmap Tasks" : null}
      icon={<Target className="w-5 h-5" />}
      loading={isLoading}
      size={config?.size ?? "medium"}
      title="Top Roadmap Tasks"
      widgetId={WIDGET_IDS.ROADMAP_TOP_TASKS}
    >
      <div className="space-y-3 w-full">
        {topTasks.length === 0 && !isLoading ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            Keine Roadmap Tasks verfügbar
          </div>
        ) : (
          topTasks.map(({ rank, score, task: roadmapTask }) => {
            const taskTitle: string = roadmapTask.title;
            const taskModule: string = roadmapTask.module;
            const taskDescription: string = roadmapTask.description;
            const taskStatus: Roadmap_Item_Status = roadmapTask.status;
            const taskType: Roadmap_Item_Type = roadmapTask.type;
            const taskImpactScore: Roadmap_Item_Score | undefined = roadmapTask.impact_score;
            const taskPriority: Roadmap_Item_Priority | undefined = roadmapTask.priority;
            const taskId: string = roadmapTask.id;

            return (
              <div
                className="p-4 rounded-lg border border-slate-600/50 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                key={taskId}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn("text-lg font-bold", getScoreColor(score))}>#{rank}</span>
                      <h4 className="text-sm font-semibold text-white truncate">{taskTitle}</h4>
                    </div>
                    {taskModule ? (
                      <p className="text-xs text-gray-400 mb-2">Module: {taskModule}</p>
                    ) : null}
                    {taskDescription ? (
                      <p className="text-xs text-gray-500 line-clamp-2 mt-1">{taskDescription}</p>
                    ) : null}
                  </div>
                  <Badge
                    className={cn("text-xs px-2 py-1 flex-shrink-0", getScoreBadgeColor(score))}
                    variant="outline"
                  >
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {score}
                  </Badge>
                </div>

                <div className="flex items-center gap-2 flex-wrap mt-3">
                  <Badge
                    className={cn("text-xs px-2 py-0.5", getStatusColor(taskStatus))}
                    variant="outline"
                  >
                    {taskStatus}
                  </Badge>
                  <Badge
                    className="text-xs px-2 py-0.5 text-gray-400 border-gray-600"
                    variant="outline"
                  >
                    {taskType}
                  </Badge>
                  {taskImpactScore ? (
                    <Badge
                      className="text-xs px-2 py-0.5 text-blue-400 border-blue-600"
                      variant="outline"
                    >
                      Impact: {taskImpactScore}
                    </Badge>
                  ) : null}
                  {taskPriority ? (
                    <Badge
                      className="text-xs px-2 py-0.5 text-purple-400 border-purple-600"
                      variant="outline"
                    >
                      Priority: {taskPriority}
                    </Badge>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>
    </WidgetContainer>
  );
};
